import React, {useEffect, useRef, useState} from 'react';
import {ActivityIndicator, Animated, Pressable, View} from 'react-native';
import ChessboardComponent from 'chessboard-package';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import type {
  Discarded,
  ReviewChapter,
} from '../../../Hooks/LmsCourseApi/useCourseReview';
import CourseVideoPlayer from '../components/CourseVideoPlayer';
import {
  legalDestinations,
  PuzzleAction,
  puzzleBoardView,
  PuzzleRun,
} from '../courseLogic';
import {formatTimestamp, squareOffset} from '../courseFormat';
import {F, S, STATUS, T} from '../courseTheme';
import {
  Agreement,
  bestArrow,
  compareWithEngine,
  CourseEvalBar,
  ENGINE_ARROW_COLOR,
  EngineState,
  EngineStatus,
  evalWord,
  formatSanLine,
  useCourseEngine,
} from '../engine';
import {nextSolutionMove, ReviewFlag, ReviewPage} from '../reviewLogic';
import DetailsPanel from './DetailsPanel';
import {ChangedField, playedSan, summaryLine} from './editorLogic';
import {E} from './editorTheme';
import {
  Button,
  Flash,
  IconButton,
  Notice,
  ring,
  useEscape,
  focusVisible,
} from './ui';

type Props = {
  chapter: ReviewChapter;
  page: ReviewPage;
  flags: ReviewFlag[];
  discarded: {index: number; item: Discarded}[];
  run: PuzzleRun;
  dispatch: (a: PuzzleAction) => void;
  engineOn: boolean;
  onEngine: (on: boolean) => void;
  detailsOpen: boolean;
  onDetails: (open: boolean) => void;
  flash: ChangedField[];
  reduced: boolean;
  notice: string | null;
  /** Size to plan for before the pane has measured itself. */
  estimate: {width: number; height: number};
  videoSrc: string | null;
  /** True while the signed link to the recording is still being fetched. */
  videoLoading: boolean;
  playhead: number | null;
  onTime: (seconds: number) => void;
  onAcceptAlternative: (san: string[]) => void;
  onKeepTitle: () => void;
  onDismiss: (flagId: number, reason: string) => void;
  onRestore: (index: number) => void;
  onEditPosition: () => void;
  onHistory: () => void;
  onFlag: () => void;
};

/** The right move, drawn as an arrow while a wrong one is held on the board. */
const CORRECT_ARROW = STATUS.good.fg;
const MIN_BOARD = 220;
const MAX_BOARD = 560;
/** Pane padding, header, summary line, toolbar and the gaps between them. */
const CHROME = 220;
/** The compact engine readout under the board. */
const ENGINE_H = 72;
/** A wrong move's banner and choices, over the summary line and toolbar they replace. */
const WRONG_H = 56;
/** Details beside the board, when the pane is wide enough… */
const DETAILS_W = 360;
/** …or the least it is given under the board. */
const DETAILS_MIN = 200;

function EngineToggle({on, onToggle}: {on: boolean; onToggle: () => void}) {
  const [focused, setFocused] = useState(false);
  return (
    <Pressable
      accessibilityRole="switch"
      accessibilityLabel="Engine"
      accessibilityState={{checked: on}}
      onPress={onToggle}
      onFocus={() => setFocused(focusVisible())}
      onBlur={() => setFocused(false)}
      style={({pressed, hovered}: any) => [
        {
          flexDirection: 'row',
          alignItems: 'center',
          gap: S[2],
          minHeight: 44,
          paddingHorizontal: S[3],
          borderRadius: 10,
          backgroundColor: pressed
            ? E.pressed
            : hovered
            ? E.hover
            : 'transparent',
          cursor: 'pointer',
        } as any,
        ring(focused),
      ]}>
      {/* A small track: filled and to the right when on. Shape and word, not colour alone. */}
      <View
        style={{
          width: 30,
          height: 18,
          borderRadius: 9,
          padding: 2,
          borderWidth: 1,
          borderColor: on ? E.accent : E.inputLine,
          backgroundColor: on ? E.accent : E.surface,
          alignItems: on ? 'flex-end' : 'flex-start',
          justifyContent: 'center',
        }}>
        <View
          style={{
            width: 12,
            height: 12,
            borderRadius: 6,
            backgroundColor: on ? F.white : E.inputLine,
          }}
        />
      </View>
      <CCText style={{fontSize: T.sm + 1, fontWeight: '600', color: E.ink}}>
        {on ? 'Engine on' : 'Engine'}
      </CCText>
    </Pressable>
  );
}

function AgreementLine({agreement}: {agreement: Agreement | null}) {
  if (!agreement) return null;
  if (agreement.kind === 'pending') {
    return (
      <View style={{flexDirection: 'row', alignItems: 'center', gap: S[2]}}>
        <Ionicons name="hourglass-outline" size={14} color={E.muted} />
        <CCText style={{fontSize: T.sm, color: E.muted}}>
          Checking the engine’s choice against the solution…
        </CCText>
      </View>
    );
  }
  const differs = agreement.kind === 'differs';
  const fg = differs ? STATUS.warn.fg : STATUS.good.fg;
  const words =
    agreement.kind === 'agrees'
      ? `Engine agrees — ${agreement.san} is its first choice`
      : agreement.kind === 'equal'
      ? `Engine agrees — rates ${agreement.san} as good as ${agreement.engineSan}`
      : `Engine prefers ${agreement.engineSan} (${agreement.engineDisplay})`;
  return (
    <View
      accessibilityLiveRegion="polite"
      style={{flexDirection: 'row', alignItems: 'center', gap: S[2]}}>
      <Ionicons
        name={differs ? 'warning' : 'checkmark-circle'}
        size={15}
        color={fg}
      />
      <CCText
        style={{flexShrink: 1, fontSize: T.sm, fontWeight: '600', color: fg}}>
        {words}
      </CCText>
    </View>
  );
}

/**
 * The engine, compact: the eval in figures and words, its line, and whether
 * it agrees with the accepted answer — two lines under the board. The
 * toolbar switch is its only control.
 */
function CompactEngine({
  state,
  status,
  agreement,
}: {
  state: EngineState;
  status: EngineStatus;
  agreement: Agreement | null;
}) {
  const best = state.lines[0];
  let body: React.ReactNode;
  if (status === 'unavailable') {
    body = (
      <CCText style={{fontSize: T.sm, color: E.muted}}>
        The engine couldn’t start in this browser. Everything else works without
        it.
      </CCText>
    );
  } else if (
    status === 'checkmate' ||
    status === 'stalemate' ||
    status === 'draw'
  ) {
    body = (
      <CCText
        style={{
          fontSize: T.sm,
          color: E.ink,
        }}>{`${status[0].toUpperCase()}${status.slice(
        1,
      )} — nothing to analyse.`}</CCText>
    );
  } else if (!best) {
    body = (
      <View style={{flexDirection: 'row', alignItems: 'center', gap: S[2]}}>
        <ActivityIndicator size="small" color={E.accent} />
        <CCText style={{fontSize: T.sm, color: E.muted}}>
          {status === 'starting' ? 'Starting the engine…' : 'Analysing…'}
        </CCText>
      </View>
    );
  } else {
    body = (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          columnGap: S[2],
          rowGap: 2,
        }}>
        <CCText
          style={{
            fontSize: T.lg,
            fontWeight: '700',
            color: E.ink,
            fontVariant: ['tabular-nums'],
          }}>
          {best.display}
        </CCText>
        <CCText style={{fontSize: T.sm + 1, fontWeight: '600', color: E.ink}}>
          {evalWord(best.score)}
        </CCText>
        <CCText style={{fontSize: T.sm, color: E.muted}}>·</CCText>
        <CCText style={{flexShrink: 1, fontSize: T.sm, color: E.ink}}>
          {formatSanLine(state.fen, best.san, 6)}
        </CCText>
        <CCText
          style={{
            fontSize: T.xs,
            color: E.muted,
            fontVariant: ['tabular-nums'],
          }}>
          {status === 'thinking' || status === 'starting'
            ? `depth ${state.depth}…`
            : `depth ${state.depth}`}
        </CCText>
      </View>
    );
  }
  return (
    <View
      accessibilityLabel="Engine analysis"
      style={{
        gap: S[1],
        paddingTop: S[2],
        borderTopWidth: 1,
        borderTopColor: E.line,
      }}>
      {body}
      {best ? <AgreementLine agreement={agreement} /> : null}
    </View>
  );
}

function MoreMenu({
  puzzle,
  onClose,
  onEditPosition,
  onHistory,
  onFlag,
}: {
  puzzle: boolean;
  onClose: () => void;
  onEditPosition: () => void;
  onHistory: () => void;
  onFlag: () => void;
}) {
  useEscape(onClose);
  const items = [
    ...(puzzle
      ? [
          {
            icon: 'create-outline',
            label: 'Edit position on board',
            run: onEditPosition,
          },
        ]
      : []),
    {icon: 'time-outline', label: 'History', run: onHistory},
    {icon: 'flag-outline', label: 'Flag for later', run: onFlag},
  ];
  return (
    <View
      accessibilityLabel="More actions for this chapter"
      style={{
        position: 'absolute',
        right: 0,
        bottom: 52,
        zIndex: 20,
        minWidth: 240,
        backgroundColor: E.surface,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: E.line,
        paddingVertical: S[1],
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 8},
        shadowOpacity: 0.14,
        shadowRadius: 24,
        elevation: 8,
      }}>
      {items.map(item => (
        <MenuItem
          key={item.label}
          icon={item.icon}
          label={item.label}
          onPress={() => {
            onClose();
            item.run();
          }}
        />
      ))}
    </View>
  );
}

function MenuItem({
  icon,
  label,
  onPress,
}: {
  icon: string;
  label: string;
  onPress: () => void;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <Pressable
      accessibilityRole="menuitem"
      accessibilityLabel={label}
      onPress={onPress}
      onFocus={() => setFocused(focusVisible())}
      onBlur={() => setFocused(false)}
      style={({pressed, hovered}: any) => [
        {
          flexDirection: 'row',
          alignItems: 'center',
          gap: S[3],
          minHeight: 44,
          paddingHorizontal: S[4],
          backgroundColor: pressed
            ? E.pressed
            : hovered
            ? E.hover
            : 'transparent',
          cursor: 'pointer',
        } as any,
        ring(focused, true),
      ]}>
      <Ionicons name={icon} size={18} color={E.ink} />
      <CCText style={{fontSize: T.sm + 1, color: E.ink}}>{label}</CCText>
    </Pressable>
  );
}

function SegmentStrip({
  start,
  end,
  playhead,
}: {
  start: number;
  end: number;
  playhead: number | null;
}) {
  const span = Math.max(1, end - start);
  const at =
    playhead == null
      ? null
      : Math.min(1, Math.max(0, (playhead - start) / span));
  return (
    <View style={{gap: S[2]}}>
      <View
        accessible
        accessibilityLabel={`Segment ${formatTimestamp(
          start,
        )} to ${formatTimestamp(end)}${
          playhead != null ? `, playhead at ${formatTimestamp(playhead)}` : ''
        }`}
        style={{height: 18, justifyContent: 'center'}}>
        <View
          style={{
            height: 4,
            borderRadius: 2,
            backgroundColor: 'rgba(38,35,34,0.14)',
          }}
        />
        {/* The segment's two ends, as ticks. */}
        <View
          style={{
            position: 'absolute',
            left: 0,
            width: 2,
            height: 18,
            borderRadius: 1,
            backgroundColor: E.ink,
          }}
        />
        <View
          style={{
            position: 'absolute',
            right: 0,
            width: 2,
            height: 18,
            borderRadius: 1,
            backgroundColor: E.ink,
          }}
        />
        {at != null ? (
          <View
            style={{
              position: 'absolute',
              left: `${at * 100}%`,
              marginLeft: -6,
              width: 12,
              height: 12,
              borderRadius: 6,
              backgroundColor: E.accent,
              borderWidth: 2,
              borderColor: F.white,
            }}
          />
        ) : null}
      </View>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          gap: S[2],
        }}>
        <CCText
          style={{
            fontSize: T.sm,
            color: E.muted,
            fontVariant: ['tabular-nums'],
          }}>{`Starts ${formatTimestamp(start)}`}</CCText>
        <CCText
          style={{
            fontSize: T.sm,
            fontWeight: '600',
            color: E.ink,
            fontVariant: ['tabular-nums'],
          }}>
          {playhead != null
            ? `Playhead ${formatTimestamp(playhead)}`
            : 'Not played yet'}
        </CCText>
        <CCText
          style={{
            fontSize: T.sm,
            color: E.muted,
            fontVariant: ['tabular-nums'],
          }}>{`Ends ${formatTimestamp(end)}`}</CCText>
      </View>
    </View>
  );
}

/**
 * The chapter under review. A puzzle is its board — played, never inside a
 * scroller — with one line under it and three quiet controls (Engine, Details,
 * ⋯). A video is the player with its segment marked. Everything else is
 * folded into Details or the ⋯ menu; the chat does the editing.
 */
export default function ChapterPane(p: Props) {
  const {page, run, dispatch} = p;
  const isPuzzle = page.content_type === 'chess_position';
  const review = page.config?.review;
  const [size, setSize] = useState<{width: number; height: number} | null>(
    null,
  );
  const [selected, setSelected] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const view = puzzleBoardView(run);
  const engine = useCourseEngine({
    fen: view.position,
    enabled: p.engineOn && isPuzzle,
    lines: 1,
  });

  // A new chapter, or a new position after an edit, starts with nothing picked up.
  useEffect(() => {
    setSelected(null);
    setMenuOpen(false);
  }, [page.id, run.board]);

  // The board fades in when an edit replaces it, so the change is seen.
  const fade = useRef(new Animated.Value(1)).current;
  const boardKey = `${page.id}-${run.board}`;
  const firstKey = useRef(boardKey);
  useEffect(() => {
    if (firstKey.current === boardKey || p.reduced) return;
    fade.setValue(0.25);
    Animated.timing(fade, {
      toValue: 1,
      duration: 220,
      useNativeDriver: false,
    }).start();
  }, [boardKey, p.reduced, fade]);

  const width = size?.width ?? p.estimate.width;
  const height = size?.height ?? p.estimate.height;
  const showBar =
    p.engineOn && ['starting', 'thinking', 'done'].includes(engine.status);
  const bar = showBar ? 22 : 0;
  // The board is the hero: it gives up room to the engine readout and to a
  // wrong move's choices, and to Details only when Details cannot sit beside it.
  const fitted = Math.min(
    MAX_BOARD,
    width - 48 - bar,
    height -
      CHROME -
      (p.engineOn ? ENGINE_H : 0) -
      (run.phase === 'wrong' ? WRONG_H : 0),
  );
  const sideBySide =
    p.detailsOpen &&
    width - 48 - bar - S[6] - DETAILS_W >=
      Math.max(MIN_BOARD, Math.min(fitted, 420));
  const boardSize = Math.round(
    Math.max(
      MIN_BOARD,
      sideBySide
        ? Math.min(fitted, width - 48 - bar - S[6] - DETAILS_W)
        : p.detailsOpen
        ? fitted - DETAILS_MIN
        : fitted,
    ),
  );
  const column = Math.min(
    Math.max(boardSize + bar, 420),
    Math.max(0, width - 48 - (sideBySide ? DETAILS_W + S[6] : 0)),
    640,
  );

  const orientation =
    ((page.config?.view as string) ?? page.content.split(' ')[1]) === 'b'
      ? 'black'
      : 'white';
  const wrong = run.phase === 'wrong' ? run.wrong : null;
  const summary = isPuzzle ? summaryLine(page) : null;
  const flashing = (f: ChangedField) => p.flash.includes(f);

  // ---- board input (click-to-move is ours; dragging is the package's) -------
  const fen = run.fen;
  const destinations = selected ? legalDestinations(fen, selected) : [];
  const move = (from: string, to: string) => {
    if (run.phase !== 'idle' || !from || !to || from === to) return;
    dispatch({type: 'move', from, to});
  };
  const onSquareClick = (square: string) => {
    if (run.phase !== 'idle') return;
    if (selected && square !== selected) {
      if (destinations.includes(square)) {
        setSelected(null);
        move(selected, square);
        return;
      }
      setSelected(legalDestinations(fen, square).length ? square : null);
      return;
    }
    if (selected === square) {
      setSelected(null);
      return;
    }
    setSelected(legalDestinations(fen, square).length ? square : null);
  };

  const engineArrow = p.engineOn ? bestArrow(engine.state) : null;
  const arrows = view.arrow
    ? [[view.arrow[0], view.arrow[1], CORRECT_ARROW]]
    : engineArrow
    ? [[engineArrow[0], engineArrow[1], ENGINE_ARROW_COLOR]]
    : undefined;
  const history = run.history.map(h => h.san);
  const agreement = review
    ? compareWithEngine(engine.state, nextSolutionMove(review.lines, history))
    : null;

  const marker = (sq: string) => squareOffset(sq, boardSize, orientation);
  const selectedAt = selected ? marker(selected) : null;
  const wrongAt = wrong ? marker(wrong.to) : null;
  const badge = wrongAt ? Math.max(16, Math.round(wrongAt.size * 0.38)) : 0;
  const progressed = run.turnStarts.length > 0 || run.phase === 'solved';
  const template =
    page.config?.title_source === 'template' &&
    !(review as any)?.title_accepted;
  const title = (
    p.chapter.title ||
    page.heading ||
    `Chapter ${p.chapter.number}`
  ).trim();

  const header = (
    <View style={{gap: 2}}>
      <CCText style={{fontSize: T.sm, color: E.muted}}>{`Chapter ${
        p.chapter.number
      } · ${isPuzzle ? 'Puzzle' : 'Video'}`}</CCText>
      <Flash
        active={flashing('title')}
        field="title"
        reduced={p.reduced}
        style={{alignSelf: 'flex-start'}}>
        <CCText
          accessibilityRole="header"
          style={{
            fontSize: T.xl,
            fontWeight: '700',
            color: E.ink,
            lineHeight: 26,
          }}>
          {title}
        </CCText>
      </Flash>
      {template ? (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: S[2],
          }}>
          <CCText style={{fontSize: T.sm, color: STATUS.warn.fg}}>
            This title was built from the board.
          </CCText>
          <Button
            label="Keep this title"
            variant="quiet"
            onPress={p.onKeepTitle}
          />
        </View>
      ) : null}
    </View>
  );

  const toolbar = (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: S[1],
        zIndex: 10,
      }}>
      {isPuzzle ? (
        <EngineToggle
          on={p.engineOn}
          onToggle={() => p.onEngine(!p.engineOn)}
        />
      ) : null}
      <Button
        label="Details"
        variant="quiet"
        trailingIcon={p.detailsOpen ? 'chevron-up' : 'chevron-down'}
        accessibilityState={{expanded: p.detailsOpen}}
        onPress={() => p.onDetails(!p.detailsOpen)}
      />
      <View style={{flex: 1}} />
      {isPuzzle && progressed ? (
        <Button
          label="Restart"
          variant="quiet"
          icon="refresh"
          onPress={() => dispatch({type: 'restart'})}
        />
      ) : null}
      <View style={{position: 'relative'}}>
        <IconButton
          icon="ellipsis-horizontal"
          label="More actions"
          accessibilityState={{expanded: menuOpen}}
          onPress={() => setMenuOpen(o => !o)}
        />
        {menuOpen ? (
          <MoreMenu
            puzzle={isPuzzle}
            onClose={() => setMenuOpen(false)}
            onEditPosition={p.onEditPosition}
            onHistory={p.onHistory}
            onFlag={p.onFlag}
          />
        ) : null}
      </View>
    </View>
  );

  const details = p.detailsOpen ? (
    <View
      style={{
        flex: 1,
        minHeight: 0,
        borderTopWidth: 1,
        borderTopColor: E.line,
      }}>
      <DetailsPanel
        page={page}
        flags={p.flags}
        discarded={p.discarded}
        onDismiss={p.onDismiss}
        onRestore={p.onRestore}
      />
    </View>
  ) : null;

  const notice = p.notice ? <Notice tone="warn" text={p.notice} /> : null;

  if (!isPuzzle) {
    const seg = page.config?.video_segment as
      | {start: number; end: number}
      | undefined;
    const videoW = Math.min(Math.max(0, width - 48), 880);
    return (
      <View
        style={{
          flex: 1,
          minHeight: 0,
          paddingHorizontal: S[6],
          paddingTop: S[5],
          paddingBottom: S[3],
        }}
        onLayout={e =>
          setSize({
            width: e.nativeEvent.layout.width,
            height: e.nativeEvent.layout.height,
          })
        }>
        <View
          style={{
            width: videoW,
            alignSelf: 'center',
            flex: 1,
            minHeight: 0,
            gap: S[3],
          }}>
          {header}
          {p.videoSrc ? (
            <CourseVideoPlayer
              src={p.videoSrc}
              segment={seg ?? null}
              onSegmentEnd={() => {}}
              onTime={p.onTime}
            />
          ) : (
            <Notice
              tone="info"
              text={
                p.videoLoading
                  ? 'Loading the recording…'
                  : 'The recording isn’t available here right now. The chapter’s times and the chat still work.'
              }
            />
          )}
          <Flash active={flashing('times')} field="times" reduced={p.reduced}>
            {seg ? (
              <SegmentStrip
                start={seg.start}
                end={seg.end}
                playhead={p.playhead}
              />
            ) : null}
          </Flash>
          {toolbar}
          {notice}
          {details}
        </View>
      </View>
    );
  }

  return (
    // A plain View, never a ScrollView: the scroll responder would claim the
    // pointer and dragging a piece would stop working. Only Details scrolls.
    <View
      style={{
        flex: 1,
        minHeight: 0,
        paddingHorizontal: S[6],
        paddingTop: S[5],
        paddingBottom: S[3],
      }}
      onLayout={e =>
        setSize({
          width: e.nativeEvent.layout.width,
          height: e.nativeEvent.layout.height,
        })
      }>
      <View
        style={{
          flex: 1,
          minHeight: 0,
          flexDirection: 'row',
          justifyContent: 'center',
          gap: S[6],
        }}>
        <View
          style={{
            width: column,
            flex: sideBySide ? undefined : 1,
            minHeight: 0,
            gap: S[3],
          }}>
          {header}

          <View style={{flexDirection: 'row', gap: S[2], alignSelf: 'center'}}>
            {showBar ? (
              <CourseEvalBar
                score={engine.state.lines[0]?.score}
                height={boardSize}
                flipped={orientation === 'black'}
              />
            ) : null}
            <Animated.View
              style={{
                width: boardSize,
                borderRadius: 4,
                overflow: 'hidden',
                opacity: fade,
              }}>
              {selectedAt ? (
                <View
                  pointerEvents="none"
                  style={{
                    position: 'absolute',
                    zIndex: 2,
                    left: selectedAt.left,
                    top: selectedAt.top,
                    width: selectedAt.size,
                    height: selectedAt.size,
                    backgroundColor: 'rgba(61,171,158,0.45)',
                  }}
                />
              ) : null}
              {destinations.map(sq => {
                const at = marker(sq);
                if (!at) return null;
                return (
                  <View
                    key={`dest-${sq}`}
                    pointerEvents="none"
                    style={{
                      position: 'absolute',
                      zIndex: 2,
                      left: at.left,
                      top: at.top,
                      width: at.size,
                      height: at.size,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    <View
                      style={{
                        width: at.size * 0.3,
                        height: at.size * 0.3,
                        borderRadius: at.size * 0.15,
                        backgroundColor: 'rgba(43,120,111,0.7)',
                      }}
                    />
                  </View>
                );
              })}
              {/* The wrong move's square: an outline and a cross, not red alone. */}
              {wrongAt ? (
                <View
                  pointerEvents="none"
                  style={{
                    position: 'absolute',
                    zIndex: 3,
                    left: wrongAt.left,
                    top: wrongAt.top,
                    width: wrongAt.size,
                    height: wrongAt.size,
                    borderWidth: 3,
                    borderColor: STATUS.bad.fg,
                    borderRadius: 4,
                  }}>
                  <View
                    style={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      width: badge,
                      height: badge,
                      borderRadius: badge / 2,
                      backgroundColor: STATUS.bad.fg,
                      borderWidth: 2,
                      borderColor: F.white,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    <Ionicons
                      name="close"
                      size={Math.round(badge * 0.7)}
                      color={F.white}
                    />
                  </View>
                </View>
              ) : null}
              <ChessboardComponent
                key={boardKey}
                position={view.position}
                lastMoveFen={view.lastMoveFen}
                boardOrientation={orientation}
                boardWidth={boardSize}
                disabled={run.phase !== 'idle'}
                handleNewFen={(m: {from: string; to: string}) =>
                  move(m?.from, m?.to)
                }
                onSquareClick={onSquareClick}
                customArrows={arrows}
              />
            </Animated.View>
          </View>

          {wrong ? (
            <View
              accessible
              accessibilityLiveRegion="polite"
              accessibilityLabel={
                run.correct
                  ? `Not the move. The move was ${run.correct.san}.`
                  : 'Not the move.'
              }
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center',
                columnGap: S[4],
                rowGap: 2,
              }}>
              <View
                style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
                <Ionicons name="close-circle" size={18} color={STATUS.bad.fg} />
                <CCText
                  style={{
                    fontSize: T.base,
                    fontWeight: '700',
                    color: STATUS.bad.fg,
                  }}>{`Not the move: ${wrong.san}.`}</CCText>
              </View>
              {run.correct ? (
                <View
                  style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
                  <Ionicons
                    name="arrow-forward-circle"
                    size={18}
                    color={CORRECT_ARROW}
                  />
                  <CCText
                    style={{
                      fontSize: T.base,
                      fontWeight: '600',
                      color: E.ink,
                    }}>{`The move was ${run.correct.san}.`}</CCText>
                </View>
              ) : null}
            </View>
          ) : run.phase === 'solved' ? (
            <View
              accessibilityLiveRegion="polite"
              style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
              <Ionicons
                name="checkmark-circle"
                size={18}
                color={STATUS.good.fg}
              />
              <CCText
                style={{fontSize: T.base, fontWeight: '600', color: E.ink}}>
                {run.helped
                  ? 'Solved with help — the line ends here.'
                  : 'Solved — the line ends here.'}
              </CCText>
            </View>
          ) : summary ? (
            <Flash
              active={flashing('solution') || flashing('position')}
              field="solution"
              reduced={p.reduced}>
              <View
                style={{
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  columnGap: S[2],
                  rowGap: 2,
                }}>
                <View
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 6,
                    borderWidth: 1,
                    borderColor: E.muted,
                    backgroundColor: summary.side.startsWith('Black')
                      ? E.ink
                      : F.white,
                  }}
                />
                <CCText
                  style={{fontSize: T.sm + 1, fontWeight: '600', color: E.ink}}>
                  {summary.side}
                </CCText>
                {summary.goal ? (
                  <>
                    <CCText style={{fontSize: T.sm + 1, color: E.muted}}>·</CCText>
                    <CCText style={{fontSize: T.sm + 1, color: E.ink}}>
                      {summary.goal}
                    </CCText>
                  </>
                ) : null}
                <CCText style={{fontSize: T.sm + 1, color: E.muted}}>·</CCText>
                <CCText selectable style={{fontSize: T.sm + 1, color: E.ink}}>
                  {summary.solution}
                </CCText>
                <CCText style={{fontSize: T.sm + 1, color: E.muted}}>·</CCText>
                <View
                  style={{flexDirection: 'row', alignItems: 'center', gap: 4}}>
                  <Ionicons
                    name={summary.machine.icon}
                    size={15}
                    color={summary.machine.fg}
                  />
                  <CCText
                    style={{
                      fontSize: T.sm + 1,
                      fontWeight: '600',
                      color: summary.machine.fg,
                    }}>
                    {summary.machine.word}
                  </CCText>
                </View>
              </View>
            </Flash>
          ) : null}

          {wrong ? (
            // Never more than four choices, and they wrap on a narrow pane.
            <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: S[2]}}>
              {run.correct ? (
                <Button
                  label="Play correct move"
                  variant="accent"
                  icon="play-forward"
                  onPress={() => dispatch({type: 'playCorrect'})}
                />
              ) : null}
              <Button
                label="Try again"
                variant="secondary"
                icon="arrow-undo"
                onPress={() => dispatch({type: 'tryAgain'})}
              />
              <Button
                label="Restart"
                variant="quiet"
                icon="refresh"
                onPress={() => dispatch({type: 'restart'})}
              />
              <Button
                label="Accept as alternative"
                variant="secondary"
                icon="git-branch-outline"
                accessibilityHint={`Adds ${playedSan(run).join(
                  ' ',
                )} as another accepted answer`}
                onPress={() => p.onAcceptAlternative(playedSan(run))}
              />
            </View>
          ) : (
            toolbar
          )}

          {notice}

          {p.engineOn ? (
            <CompactEngine
              state={engine.state}
              status={engine.status}
              agreement={agreement}
            />
          ) : null}

          {sideBySide ? null : details}
        </View>
        {sideBySide ? (
          <View
            style={{
              width: DETAILS_W,
              minHeight: 0,
              borderLeftWidth: 1,
              borderLeftColor: E.line,
              paddingLeft: S[4],
            }}>
            <DetailsPanel
              page={page}
              flags={p.flags}
              discarded={p.discarded}
              onDismiss={p.onDismiss}
              onRestore={p.onRestore}
            />
          </View>
        ) : null}
      </View>
      {/* Headless — renders nothing; null while the engine is off. */}
      {engine.engineElement}
    </View>
  );
}
